import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, Clock, Loader2, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  fetchScheduledReports, createScheduledReport, updateScheduledReport, deleteScheduledReport,
  type ScheduledReport,
} from "@/lib/reseller-api";
import { type Device } from "@/lib/traccar-api";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: Device[];
}

const SCHEDULABLE_TYPES = ['trips', 'events', 'stops', 'summary', 'hours', 'speeding', 'idle'];

const EMPTY: Partial<ScheduledReport> = {
  name: '',
  report_type: 'trips',
  device_id: null,
  frequency: 'weekly',
  day_of_week: 1,
  day_of_month: null,
  time_of_day: '08:00',
  date_range: 'last_week',
  recipients: [],
  enabled: true,
};

export function ScheduledReportsDialog({ open, onOpenChange, devices }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ScheduledReport | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState<Partial<ScheduledReport>>(EMPTY);
  const [recipientInput, setRecipientInput] = useState('');

  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: fetchScheduledReports,
    enabled: open,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });

  const createMutation = useMutation({
    mutationFn: createScheduledReport,
    onSuccess: () => { invalidate(); closeForm(); toast.success(t('reports.scheduled.created')); },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ScheduledReport> }) => updateScheduledReport(id, data),
    onSuccess: () => { invalidate(); closeForm(); toast.success(t('reports.scheduled.updated')); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteScheduledReport,
    onSuccess: () => { invalidate(); toast.success(t('reports.scheduled.deleted')); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => updateScheduledReport(id, { enabled }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setRecipientInput('');
    setIsFormOpen(true);
  };

  const openEdit = (s: ScheduledReport) => {
    setEditing(s);
    setForm({ ...s });
    setRecipientInput('');
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(null);
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if ((form.recipients || []).includes(email)) return;
    setForm(f => ({ ...f, recipients: [...(f.recipients || []), email] }));
    setRecipientInput('');
  };

  const removeRecipient = (email: string) => {
    setForm(f => ({ ...f, recipients: (f.recipients || []).filter(r => r !== email) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.report_type || !form.frequency) return;
    const payload = { ...form };
    if (payload.frequency !== 'weekly') payload.day_of_week = null;
    if (payload.frequency !== 'monthly') payload.day_of_month = null;
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const frequencyLabel = (s: ScheduledReport) => {
    if (s.frequency === 'daily') return t('reports.scheduled.daily');
    if (s.frequency === 'weekly') {
      const days = [t('common.sun'), t('common.mon'), t('common.tue'), t('common.wed'), t('common.thu'), t('common.fri'), t('common.sat')];
      return `${t('reports.scheduled.weekly')} (${days[s.day_of_week ?? 1]})`;
    }
    return `${t('reports.scheduled.monthly')} (${t('reports.scheduled.dayOfMonth', { day: s.day_of_month ?? 1 })})`;
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('reports.scheduled.title')}</DialogTitle>
        </DialogHeader>

        {!isFormOpen ? (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" onClick={openNew}>
                <Plus className="h-4 w-4 mr-1" /> {t('reports.scheduled.add')}
              </Button>
            </div>
            {isLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : schedules.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">{t('reports.scheduled.none')}</p>
            ) : (
              <div className="space-y-2">
                {schedules.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                    <Switch
                      checked={s.enabled}
                      onCheckedChange={(enabled) => toggleMutation.mutate({ id: s.id, enabled })}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{s.name}</span>
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          {t(`reports.reportTypes.${s.report_type}`, { defaultValue: s.report_type })}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{frequencyLabel(s)} · {s.time_of_day.slice(0, 5)}</span>
                        <span>· {t(`reports.scheduled.dateRanges.${s.date_range}`)}</span>
                        {s.recipients.length > 0 && <span>· {s.recipients.length} {t('reports.scheduled.recipients')}</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(s)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { if (confirm(t('reseller.common.deleteConfirm'))) deleteMutation.mutate(s.id); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>{t('reports.scheduled.name')}</Label>
                <Input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>

              <div className="space-y-2">
                <Label>{t('reports.reportType')}</Label>
                <Select value={form.report_type} onValueChange={v => setForm(f => ({ ...f, report_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCHEDULABLE_TYPES.map(rt => (
                      <SelectItem key={rt} value={rt}>{t(`reports.reportTypes.${rt}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('history.selectDevice')}</Label>
                <Select
                  value={form.device_id != null ? String(form.device_id) : 'all'}
                  onValueChange={v => setForm(f => ({ ...f, device_id: v === 'all' ? null : Number(v) }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    {devices.map(d => <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('reports.scheduled.frequency')}</Label>
                <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v as ScheduledReport['frequency'] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t('reports.scheduled.daily')}</SelectItem>
                    <SelectItem value="weekly">{t('reports.scheduled.weekly')}</SelectItem>
                    <SelectItem value="monthly">{t('reports.scheduled.monthly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>{t('reports.scheduled.dayOfWeek')}</Label>
                  <Select value={String(form.day_of_week ?? 1)} onValueChange={v => setForm(f => ({ ...f, day_of_week: Number(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[t('common.sun'), t('common.mon'), t('common.tue'), t('common.wed'), t('common.thu'), t('common.fri'), t('common.sat')].map((d, i) => (
                        <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label>{t('reports.scheduled.dayOfMonth')}</Label>
                  <Select value={String(form.day_of_month ?? 1)} onValueChange={v => setForm(f => ({ ...f, day_of_month: Number(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('reports.scheduled.time')}</Label>
                <Input type="time" value={form.time_of_day?.slice(0, 5) || '08:00'} onChange={e => setForm(f => ({ ...f, time_of_day: e.target.value }))} />
              </div>

              <div className="space-y-2">
                <Label>{t('reports.scheduled.dateRange')}</Label>
                <Select value={form.date_range} onValueChange={v => setForm(f => ({ ...f, date_range: v as ScheduledReport['date_range'] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_day">{t('reports.scheduled.dateRanges.last_day')}</SelectItem>
                    <SelectItem value="last_week">{t('reports.scheduled.dateRanges.last_week')}</SelectItem>
                    <SelectItem value="last_month">{t('reports.scheduled.dateRanges.last_month')}</SelectItem>
                    <SelectItem value="last_30_days">{t('reports.scheduled.dateRanges.last_30_days')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('reports.scheduled.recipientsLabel')}</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t('reports.scheduled.recipientPlaceholder')}
                  value={recipientInput}
                  onChange={e => setRecipientInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                />
                <Button type="button" variant="outline" size="icon" onClick={addRecipient}>
                  <Check className="h-4 w-4" />
                </Button>
              </div>
              {(form.recipients || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(form.recipients || []).map(email => (
                    <Badge key={email} variant="secondary" className="gap-1">
                      {email}
                      <button type="button" onClick={() => removeRecipient(email)}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1" disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editing ? t('common.update') : t('common.create')}
              </Button>
              <Button type="button" variant="outline" onClick={closeForm}>{t('common.cancel')}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
